import React, { useMemo, useState } from 'react';
import type Highcharts from 'highcharts';
import { Button, SpaceBetween, Container, Header, Alert, Box, ColumnLayout } from '@amzn/awsui-components-core';
import { HighchartsChart } from './charts/HighchartsChart';
import './cross-team-comparison.scss';
import { ConnectionsScorecard, CONNECTIONS_THEMES, THEME_DISPLAY_NAMES, Theme } from './types';
import { CONNECTIONS_COLORS, RANGE_STATUS_COLOR, LEADER_SERIES_PALETTE } from './connectionsColors';
import { getBadgeUrl } from './connectionsBadge';

interface CrossTeamComparisonProps {
  scorecards: ConnectionsScorecard[];
  period?: string;
}

// Range-status labels for per-bar tooltip (colors live in connectionsColors.ts).
const STATUS_LABEL: Record<'above' | 'within' | 'below', string> = {
  above: '▲ Above expected range',
  within: '● Within expected range',
  below: '▼ Below expected range'
};

/**
 * Cross-team comparison rendered as a single grouped column chart:
 * x-axis = the 4 Connections themes, one colored column per leader within each
 * group, and a legend showing each leader's badge photo + name.
 *
 * Dark mode handled by the HighchartsChart wrapper.
 */
export const CrossTeamComparison: React.FC<CrossTeamComparisonProps> = ({ scorecards, period }) => {
  // Clickable theme filter — default all 4 selected. Toggling controls which
  // theme groups show on the grouped chart; Highcharts auto-centers/distributes
  // the remaining groups. At least one theme must stay selected.
  const [selectedThemes, setSelectedThemes] = useState<Theme[]>([...CONNECTIONS_THEMES]);
  const toggleTheme = (theme: Theme) => {
    setSelectedThemes((prev) => {
      if (prev.includes(theme)) {
        const next = prev.filter((t) => t !== theme);
        return next.length === 0 ? prev : next; // keep at least one
      }
      // preserve canonical theme order
      return CONNECTIONS_THEMES.filter((t) => prev.includes(t) || t === theme);
    });
  };

  // "Below expected range" focus: clicking a count card filters the chart to
  // ONLY that theme AND only the leaders below the expected range. Clicking the
  // same card again clears it. When active, it overrides the theme-toggle row.
  const [belowRangeTheme, setBelowRangeTheme] = useState<Theme | null>(null);
  const toggleBelowRange = (theme: Theme) => setBelowRangeTheme((prev) => (prev === theme ? null : theme));

  // Effective themes shown on the chart (focus mode = single theme).
  const effThemes = useMemo<Theme[]>(() => (belowRangeTheme ? [belowRangeTheme] : selectedThemes), [belowRangeTheme, selectedThemes]);

  // Stable per-leader color keyed by original order, so colors don't shift when
  // the leader set is filtered down in focus mode.
  const colorOf = (login: string) => {
    const idx = scorecards.findIndex((s) => s.leaderLogin === login);
    return LEADER_SERIES_PALETTE[(idx < 0 ? 0 : idx) % LEADER_SERIES_PALETTE.length];
  };

  const filtersActive = belowRangeTheme !== null || selectedThemes.length !== CONNECTIONS_THEMES.length;
  const resetFilters = () => {
    setBelowRangeTheme(null);
    setSelectedThemes([...CONNECTIONS_THEMES]);
  };

  // Per-theme: list of leaders below the expected range (for the count cards).
  const themeStats = useMemo(() => {
    const stats: Record<Theme, { belowNames: string[] }> = {
      CAREER_GROWTH: { belowNames: [] },
      CULTURE: { belowNames: [] },
      INCLUSION: { belowNames: [] },
      JOB_SATISFACTION: { belowNames: [] }
    };
    scorecards.forEach((sc) => {
      sc.themes.forEach((t) => {
        const themeKey = t.theme.toUpperCase() as Theme;
        if (!(themeKey in stats)) return;
        if (t.rangeIndicator === 'below') stats[themeKey].belowNames.push(sc.displayName || sc.leaderLogin);
      });
    });
    return stats;
  }, [scorecards]);

  // One series per leader; each point carries range status (for the border
  // color) + sample/population/response-rate (for the rich tooltip).
  // In below-range focus, only leaders below expected range for that theme show.
  const series = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    const effScorecards = belowRangeTheme
      ? scorecards.filter((sc) => {
          const t = sc.themes.find((th) => th.theme.toUpperCase() === belowRangeTheme);
          return t?.rangeIndicator === 'below';
        })
      : scorecards;
    return effScorecards.map((sc) => {
      const data: Highcharts.PointOptionsObject[] = effThemes.map((theme) => {
        const t = sc.themes.find((th) => th.theme.toUpperCase() === theme);
        if (!t || t.metricValue == null) return { y: null };
        const status = (t.rangeIndicator || null) as 'above' | 'within' | 'below' | null;
        const sample = t.sampleSize;
        const population = t.populationSize;
        const responseRate = sample != null && population != null && population > 0 ? (sample / population) * 100 : null;
        return {
          y: Math.round(t.metricValue * 10000) / 100,
          custom: {
            status,
            min: t.benchmarkMin,
            max: t.benchmarkMax,
            sample,
            population,
            responseRate
          }
        };
      });
      return {
        type: 'column',
        name: sc.displayName || sc.leaderLogin,
        data,
        color: colorOf(sc.leaderLogin),
        custom: { login: sc.leaderLogin }
      } as Highcharts.SeriesColumnOptions;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scorecards, effThemes, belowRangeTheme]);

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: { type: 'column' },
      xAxis: {
        categories: effThemes.map((t) => THEME_DISPLAY_NAMES[t]),
        labels: { style: { fontSize: '12px', fontWeight: '500' } },
        lineWidth: 1
      },
      yAxis: {
        min: 0,
        max: 100,
        tickInterval: 50,
        labels: { format: '{value}%' },
        title: { text: undefined }
      },
      tooltip: {
        useHTML: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: function (this: any) {
          const c = (this.point?.options?.custom || {}) as {
            status?: 'above' | 'within' | 'below' | null;
            min?: number | null;
            max?: number | null;
            sample?: number | null;
            population?: number | null;
            responseRate?: number | null;
          };
          const statusTxt = c.status ? STATUS_LABEL[c.status] : '';
          const statusCol = c.status ? RANGE_STATUS_COLOR[c.status] : CONNECTIONS_COLORS.NEUTRAL;
          const range = c.min != null && c.max != null ? `${(c.min * 100).toFixed(1)}% - ${(c.max * 100).toFixed(1)}%` : 'n/a';
          const sampleStr = c.sample != null ? c.sample.toLocaleString() : 'n/a';
          const popStr = c.population != null ? c.population.toLocaleString() : 'n/a';
          const rrStr = c.responseRate != null ? `${c.responseRate.toFixed(1)}%` : 'n/a';
          const y = typeof this.y === 'number' ? this.y.toFixed(1) : '?';
          return (
            `<div style="font-size:11px;line-height:1.5">` +
            `<div style="font-weight:600;margin-bottom:2px">${this.key}</div>` +
            `<div><span style="color:${this.color}">●</span> ${this.series.name}</div>` +
            `<div>Score: <b>${y}%</b> &nbsp;<span style="color:${statusCol};font-weight:600">${statusTxt}</span></div>` +
            `<div>Expected range: ${range}</div>` +
            `<div>Sample: ${sampleStr}</div>` +
            `<div>Population: ${popStr}</div>` +
            `<div>Response rate: ${rrStr}</div>` +
            `</div>`
          );
        }
      },
      plotOptions: {
        column: {
          borderRadius: 3,
          borderWidth: 0,
          groupPadding: 0.12,
          pointPadding: 0.02,
          states: { hover: { brightness: 0.1 } }
        }
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 6,
        symbolHeight: 12,
        symbolWidth: 12,
        itemDistance: 24,
        useHTML: true,
        // Clicking a leader toggles the series; show the hidden name struck through (not just greyed) -- matches the Attrition Trend legend.
        itemHiddenStyle: { color: CONNECTIONS_COLORS.MUTED_HIDDEN, textDecoration: 'line-through' },
        labelFormatter: function (this: Highcharts.Point | Highcharts.Series) {
          const self = this as Highcharts.Series;
          const login = (self.options as { custom?: { login?: string } }).custom?.login || '';
          const url = getBadgeUrl(login);
          return (
            // Inline layout (NOT inline-flex): a flex container blocks text-decoration propagation,
            // so itemHiddenStyle's line-through wouldn't reach the name. Match the Attrition Trend legend.
            `<img src="${url}" width="22" height="22" style="border-radius:50%;object-fit:cover;border:1px solid rgba(0,0,0,0.12);vertical-align:middle;margin-right:7px" ` +
            `onerror="this.style.display='none'" alt="" />` +
            `<span style="vertical-align:middle">${self.name}</span>`
          );
        }
      },
      series
    }),
    [series, effThemes]
  );

  if (scorecards.length === 0) {
    return (
      <Alert type="info" header="No cross-team data">
        No direct-org breakdown is available for the selected period and scope.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="m">
      <Container
        header={
          <Header
            variant="h3"
            actions={filtersActive ? <Button onClick={resetFilters}>Reset filters</Button> : undefined}
            description="Click a theme to focus the chart on just its below-range leaders"
          >
            Direct reports below expected range{period ? ` - ${period}` : ''}
          </Header>
        }
      >
        <ColumnLayout columns={4} variant="text-grid">
          {CONNECTIONS_THEMES.map((theme) => {
            const count = themeStats[theme].belowNames.length;
            const active = belowRangeTheme === theme;
            const clickable = count > 0;
            return (
              <div
                key={theme}
                onClick={clickable ? () => toggleBelowRange(theme) : undefined}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable ? (e) => (e.key === 'Enter' || e.key === ' ') && toggleBelowRange(theme) : undefined}
                className={['ctc-card', clickable && 'ctc-card--clickable', active && 'ctc-card--active'].filter(Boolean).join(' ')}
              >
                <Box variant="awsui-key-label">{THEME_DISPLAY_NAMES[theme]}</Box>
                <Box variant="h2" color={count > 0 ? 'text-status-error' : 'text-status-success'} display="inline">
                  {count}{' '}
                  <Box variant="span" fontSize="body-s" color="text-body-secondary">
                    / {scorecards.length}
                  </Box>
                </Box>
                {clickable && (
                  <Box fontSize="body-s" color={active ? 'text-status-error' : 'text-body-secondary'} margin={{ top: 'xxs' }}>
                    {active ? 'Filtering ✓' : 'Click to filter'}
                  </Box>
                )}
              </div>
            );
          })}
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h3">Theme Scores by Leader{period ? ` - ${period}` : ''}</Header>}>
        <Box margin={{ bottom: 's' }}>
          <SpaceBetween direction="horizontal" size="xs">
            {CONNECTIONS_THEMES.map((theme) => (
              <Button
                key={theme}
                variant={selectedThemes.includes(theme) ? 'primary' : 'normal'}
                disabled={belowRangeTheme !== null}
                onClick={() => toggleTheme(theme)}
              >
                {THEME_DISPLAY_NAMES[theme]}
              </Button>
            ))}
          </SpaceBetween>
        </Box>
        {belowRangeTheme && (
          <Box fontSize="body-s" color="text-status-error" margin={{ bottom: 'xs' }}>
            Focused on directs <strong>below expected range</strong> for {THEME_DISPLAY_NAMES[belowRangeTheme]}. Click the highlighted card again, or
            Reset filters, to show everything.
          </Box>
        )}
        <div className="ctc-chart-wrap">
          <HighchartsChart options={options} height={Math.max(420, scorecards.length * 8 + 360)} />
        </div>
        <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xs' }}>
          Hover a bar for score status, expected range, sample, population, and response rate.
        </Box>
      </Container>
    </SpaceBetween>
  );
};
